import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { TTnAOrderTemplateActionType } from './tna-order-template.models';
import * as TnAOrderTemplateActions from './tna-order-template.actions';
import { from, of } from 'rxjs';
import { TnAOrderTemplateFacade } from './tna-order-template.facade';
import { TPlani8State } from '../reducers';
import { Store } from '@ngrx/store';
import { ExcelUtilClass } from '@monorepo/utils';

@Injectable({ providedIn: 'root' })
export class TnAOrderTemplateEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private tnaOrderTemplateFacade: TnAOrderTemplateFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private excelHelper: ExcelUtilClass,
    private store: Store<TPlani8State>,
  ) {}

  getTnAOrderTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnAOrderTemplateActionType.GetTnAOrderTemplates),
      switchMap((action: TnAOrderTemplateActions.GetTnAOrderTemplates) => {
        if (action.showLocalLoader) {
          this.tnaOrderTemplateFacade.setShowTnAOrderTemplatesLoader(true);
          return of([null, action] as const);
        } else {
          return from(this.appNotificationUtilClass.presentLoading()).pipe(
            map((loaderEl) => {
              return [loaderEl, action] as const;
            }),
          );
        }
      }),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.getTnAOrderTemplate(action.tnaOrderTemplateId).pipe(
          finalize(() => {
            action.showLocalLoader
              ? this.tnaOrderTemplateFacade.setShowTnAOrderTemplatesLoader(false)
              : loaderEl && this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TnAOrderTemplateActions.GetTnAOrderTemplatesSuccess(res);
          }),
          catchError(() => {
            return of(new TnAOrderTemplateActions.GetTnAOrderTemplatesFailure());
          }),
        );
      }),
    ),
  );

  autoUpdateTargetDates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnAOrderTemplateActionType.AutoUpdateTargetDates),
      switchMap((action: TnAOrderTemplateActions.AutoUpdateTargetDates) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.autoUpdateTnaTargetDates(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.appNotificationUtilClass.presentToast('Target dates succesfully updated');
            return new TnAOrderTemplateActions.GetTnAOrderTemplates(action.orderTnaId);
          }),
          catchError(() => {
            return of(new TnAOrderTemplateActions.AutoUpdateTargetDatesFailure());
          }),
        ),
      ),
    ),
  );

  updateTnAOrderTemplateStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnAOrderTemplateActionType.AddUpdateTnAOrderTemplate),
      switchMap((action: TnAOrderTemplateActions.AddUpdateTnAOrderTemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateTnAOrderTemplateStep(action.updateTnaOrderTemplateReq).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            const orderTnaId = (action.updateTnaOrderTemplateReq?.['orderTnaId'] as string) || '';
            return new TnAOrderTemplateActions.GetTnAOrderTemplates(orderTnaId);
          }),
          catchError(() => {
            return of(new TnAOrderTemplateActions.AddUpdateTnAOrderTemplateFailure());
          }),
        ),
      ),
    ),
  );

  deleteTnAOrderTemplateStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnAOrderTemplateActionType.DeleteTnAOrderTemplate),
      switchMap((action: TnAOrderTemplateActions.DeleteTnAOrderTemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteTnAOrderTemplateStep(action.deleteTnaOrderTemplateReq).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            const msg = res['message'] ? res['message'] : 'Order TnA step Deleted.';
            this.appNotificationUtilClass.presentToast(msg);
            return new TnAOrderTemplateActions.GetTnAOrderTemplates(
              action.deleteTnaOrderTemplateReq?.['orderTnaId'] as string,
            );
          }),
          catchError(() => {
            return of(new TnAOrderTemplateActions.DeleteTnAOrderTemplateFailure());
          }),
        ),
      ),
    ),
  );

  downloadTnaOrderTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnAOrderTemplateActionType.DownloadTnaOrderTemplate),
      switchMap((action: TnAOrderTemplateActions.DownloadTnaOrderTemplate) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        return this.plani8DataService.downloadOrdetTnaTemplate(action.templateId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.excelHelper.downloadFile(res, 'tna-order-template-details.xlsx');
            return new TnAOrderTemplateActions.DownloadTnaOrderTemplateSuccess();
          }),
          catchError(() => of(new TnAOrderTemplateActions.DownloadTnaOrderTemplateFailure())),
        );
      }),
    ),
  );

  updateStepByTemplateId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnAOrderTemplateActionType.UpdateStepByTemplateId),
      switchMap((action: TnAOrderTemplateActions.UpdateStepByTemplateId) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateStepByTemplateId(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map(() => new TnAOrderTemplateActions.GetTnAOrderTemplates(action.orderTnaId)),
          catchError(() => of(new TnAOrderTemplateActions.UpdateStepByTemplateIdFailure())),
        ),
      ),
    ),
  );
}
